import React, { useState } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { api } from '../services/api.ts'
import { toast } from 'react-hot-toast'
import {
  DocumentArrowUpIcon,
  CloudArrowUpIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

interface ImportResponse {
  success: boolean
  message: string
  total_stores: number
  inserted_count: number
  duplicate_count: number
  job_id: string
  filename: string
}

const Import: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [description, setDescription] = useState('')
  const [dragActive, setDragActive] = useState(false)
  const queryClient = useQueryClient()

  const importMutation = useMutation(
    async (formData: FormData) => {
      const response = await api.post('/api/export/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      return response.data as ImportResponse
    },
    {
      onSuccess: (data) => {
        toast.success(data.message)
        setSelectedFile(null)
        setDescription('')
        // Invalidate queries to refresh job lists
        queryClient.invalidateQueries('all-jobs')
        queryClient.invalidateQueries('jobs')
      },
      onError: (error: any) => {
        const message = error.response?.data?.detail || 'Failed to import file'
        toast.error(message)
      },
    }
  )

  const handleFileSelect = (file: File) => {
    const validTypes = ['.csv', '.xlsx', '.xls']
    const fileExtension = file.name
      .toLowerCase()
      .substring(file.name.lastIndexOf('.'))

    if (!validTypes.includes(fileExtension)) {
      toast.error('Please select a CSV or Excel file (.csv, .xlsx, .xls)')
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      // 10MB
      toast.error('File size must be less than 10MB')
      return
    }

    setSelectedFile(file)
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleRemoveFile = () => {
    setSelectedFile(null)
    // Reset the file input
    const fileInput = document.getElementById('file-upload') as HTMLInputElement
    if (fileInput) {
      fileInput.value = ''
    }
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    const file = e.dataTransfer.files?.[0]
    if (file) {
      handleFileSelect(file)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedFile) {
      toast.error('Please select a file to import')
      return
    }

    const formData = new FormData()
    formData.append('file', selectedFile)
    formData.append('description', description)

    importMutation.mutate(formData)
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className='space-y-6'>
      <div className='md:flex md:items-center md:justify-between'>
        <div className='flex-1 min-w-0'>
          <h2 className='text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:truncate'>
            Import Data
          </h2>
          <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
            Upload CSV or Excel files to import store data into the system
          </p>
        </div>
      </div>

      <div className='bg-white dark:bg-gray-800 shadow rounded-lg'>
        <div className='px-4 py-5 sm:p-6'>
          <form onSubmit={handleSubmit} className='space-y-6'>
            {/* File Upload Area */}
            <div>
              <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                Select File
              </label>
              <div
                className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
                  dragActive
                    ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                    : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className='text-center'>
                  <CloudArrowUpIcon className='mx-auto h-12 w-12 text-gray-400 dark:text-gray-500' />
                  <div className='mt-4'>
                    <label htmlFor='file-upload' className='cursor-pointer'>
                      <span className='mt-2 block text-sm font-medium text-gray-900 dark:text-white'>
                        {selectedFile
                          ? selectedFile.name
                          : 'Drop files here or click to browse'}
                      </span>
                      <input
                        id='file-upload'
                        name='file-upload'
                        type='file'
                        className='sr-only'
                        accept='.csv,.xlsx,.xls'
                        onChange={handleFileChange}
                      />
                    </label>
                    <p className='mt-1 text-xs text-gray-500 dark:text-gray-400'>
                      CSV, XLS, XLSX up to 10MB
                    </p>
                  </div>
                </div>
              </div>

              {selectedFile && (
                <div className='mt-4 flex items-center justify-between bg-gray-50 dark:bg-gray-700 rounded-lg p-3'>
                  <div className='flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400'>
                    <DocumentTextIcon className='h-5 w-5' />
                    <span>{selectedFile.name}</span>
                    <span>({formatFileSize(selectedFile.size)})</span>
                  </div>
                  <button
                    type='button'
                    onClick={handleRemoveFile}
                    className='flex items-center space-x-1 text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors'
                  >
                    <XMarkIcon className='h-4 w-4' />
                    <span>Remove</span>
                  </button>
                </div>
              )}
            </div>

            {/* Description */}
            <div>
              <label
                htmlFor='description'
                className='block text-sm font-medium text-gray-700 dark:text-gray-300'
              >
                Description (Optional)
              </label>
              <div className='mt-1'>
                <textarea
                  id='description'
                  name='description'
                  rows={3}
                  className='shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white'
                  placeholder='Add a description for this import...'
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </div>
            </div>

            {/* File Format Requirements */}
            <div className='bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4'>
              <div className='flex'>
                <div className='flex-shrink-0'>
                  <DocumentArrowUpIcon className='h-5 w-5 text-blue-400' />
                </div>
                <div className='ml-3'>
                  <h3 className='text-sm font-medium text-blue-800 dark:text-blue-200'>
                    Required File Format
                  </h3>
                  <div className='mt-2 text-sm text-blue-700 dark:text-blue-300'>
                    <p className='font-medium'>Required columns:</p>
                    <ul className='list-disc list-inside mt-1 space-y-1'>
                      <li>
                        <strong>Company Name</strong> (or Company, Business
                        Name, Store Name) - Store/business name
                      </li>
                      <li>
                        <strong>Phone</strong> (or Phone Number, Contact,
                        Telephone) - Contact phone number
                      </li>
                    </ul>
                    <p className='font-medium mt-2'>Optional columns:</p>
                    <ul className='list-disc list-inside mt-1 space-y-1'>
                      <li>
                        <strong>Street</strong> - Street address
                      </li>
                      <li>
                        <strong>City</strong> - City name
                      </li>
                      <li>
                        <strong>State</strong> - State abbreviation
                      </li>
                      <li>
                        <strong>Zip</strong> - ZIP code
                      </li>
                      <li>
                        <strong>website</strong> - Website URL
                      </li>
                      <li>
                        <strong>Place ID</strong> - Google Places ID
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className='flex justify-end'>
              <button
                type='submit'
                disabled={!selectedFile || importMutation.isLoading}
                className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                {importMutation.isLoading ? (
                  <>
                    <svg
                      className='animate-spin -ml-1 mr-3 h-5 w-5 text-white'
                      xmlns='http://www.w3.org/2000/svg'
                      fill='none'
                      viewBox='0 0 24 24'
                    >
                      <circle
                        className='opacity-25'
                        cx='12'
                        cy='12'
                        r='10'
                        stroke='currentColor'
                        strokeWidth='4'
                      ></circle>
                      <path
                        className='opacity-75'
                        fill='currentColor'
                        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                      ></path>
                    </svg>
                    Importing...
                  </>
                ) : (
                  <>
                    <DocumentArrowUpIcon className='h-5 w-5 mr-2' />
                    Import Data
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Import Results */}
      {importMutation.isSuccess && (
        <div className='bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-4'>
          <div className='flex'>
            <div className='flex-shrink-0'>
              <CheckCircleIcon className='h-5 w-5 text-green-400' />
            </div>
            <div className='ml-3'>
              <h3 className='text-sm font-medium text-green-800 dark:text-green-200'>
                Import Successful
              </h3>
              <div className='mt-2 text-sm text-green-700 dark:text-green-300'>
                <p>{importMutation.data?.message}</p>
                <div className='mt-2 space-y-1'>
                  <p>
                    • Total stores processed:{' '}
                    {importMutation.data?.total_stores}
                  </p>
                  <p>
                    • Successfully imported:{' '}
                    {importMutation.data?.inserted_count}
                  </p>
                  {importMutation.data?.duplicate_count > 0 && (
                    <p>
                      • Duplicates skipped:{' '}
                      {importMutation.data?.duplicate_count}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import Error */}
      {importMutation.isError && (
        <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4'>
          <div className='flex'>
            <div className='flex-shrink-0'>
              <ExclamationTriangleIcon className='h-5 w-5 text-red-400' />
            </div>
            <div className='ml-3'>
              <h3 className='text-sm font-medium text-red-800 dark:text-red-200'>
                Import Failed
              </h3>
              <div className='mt-2 text-sm text-red-700 dark:text-red-300'>
                <p>
                  {importMutation.error?.response?.data?.detail ||
                    'An error occurred during import'}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Import
